type LogMethod = (...args: unknown[]) => void

export type LogLevel = "error" | "warn" | "info" | "debug"
export type LogLevelConfig = LogLevel | "none" | "all"

export type LogWriter = (level: LogLevel, ...args: unknown[]) => void

export interface Logger {
  error: LogMethod
  info: LogMethod
  warn: LogMethod
  debug: LogMethod
  subLogger: (prefix: string) => Logger
}

// in order from lowest to highest (order is used for comparing)
export const logConfigLevels: LogLevelConfig[] = [
  "all",
  "debug",
  "info",
  "warn",
  "error",
  "none",
]

const isLevelEnabled = (
  requestedLevel: LogLevel,
  configuredLevel: LogLevelConfig,
) =>
  logConfigLevels.indexOf(requestedLevel) >=
  logConfigLevels.indexOf(configuredLevel)

const levelCheckedLogMethod = (
  requestedLevel: LogLevel,
  configuredLevel: LogLevelConfig,
  writer: LogWriter,
  prefixes: string[],
) => {
  if (!isLevelEnabled(requestedLevel, configuredLevel)) {
    return () => {}
  }
  return uncheckedLogMethod(requestedLevel, configuredLevel, writer, prefixes)
}

const uncheckedLogMethod =
  (
    requestedLevel: LogLevel,
    _configuredLevel: LogLevelConfig,
    writer: LogWriter,
    prefixes: string[],
  ) =>
  (...args: unknown[]) => {
    const logArgs = [...prefixes, ...args]
    writer(requestedLevel, ...logArgs)
  }

const matcherRegex = (pattern: string) =>
  new RegExp(pattern.replace(/\*/g, ".*?"))

const isDebugMatch = (debugEnv: string, prefixes: string[]) => {
  const includes = [] as RegExp[]

  for (const pattern of debugEnv.split(",")) {
    if (!pattern) continue
    const isExclude = pattern.startsWith("-")
    if (isExclude) {
      const shouldExclude = matcherRegex(pattern.substring(1))
      if (prefixes.some((p) => shouldExclude.test(p))) return false
    } else {
      includes.push(matcherRegex(pattern))
    }
  }
  return prefixes.some((p) => includes.some((include) => include.test(p)))
}

export const makeLogger = (
  level: LogLevelConfig,
  writer: LogWriter,
  debugEnv = "",
  prefixes: string[] = [],
): Logger => {
  const isMatch = isDebugMatch(debugEnv, prefixes)
  const writerMethod = isMatch ? uncheckedLogMethod : levelCheckedLogMethod

  return {
    subLogger: (subPrefix: string) =>
      makeLogger(
        level,
        writer,
        debugEnv,
        subPrefix ? prefixes.concat([`[${subPrefix}]`]) : prefixes,
      ),
    error: writerMethod("error", level, writer, prefixes),
    warn: writerMethod("warn", level, writer, prefixes),
    info: writerMethod("info", level, writer, prefixes),
    debug: writerMethod("debug", level, writer, prefixes),
  }
}
